import React, { useState } from 'react';
import {
  shareToInstagram,
  shareToTikTok,
  shareToWhatsApp,
  shareToFacebook,
  shareToTwitter,
  downloadImageWithWatermark,
  copyShareLink
} from '../utils/imageUtils';
import './ShareModal.css';

const ShareModal = ({ isOpen, onClose, imageUrl, imageName = 'tryon' }) => {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState(''); // 'success' or 'error'

  if (!isOpen) return null;

  const showMessage = (text, type = 'success') => {
    setMessage(text);
    setMessageType(type);
    setTimeout(() => {
      setMessage('');
      setMessageType('');
    }, 3000);
  };

  const handleInstagramShare = async () => {
    setLoading(true);
    try {
      const result = await shareToInstagram(imageUrl);
      if (result.success) {
        showMessage(result.message || 'Shared successfully!', 'success');
      } else {
        if (result.cancelled) {
          showMessage('Share cancelled', 'error');
        } else {
          showMessage(result.details || 'Could not share. Image download attempted.', 'error');
        }
      }
    } catch (error) {
      showMessage('Error: ' + (error.message || 'Failed to share'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleTikTokShare = async () => {
    setLoading(true);
    try {
      const result = await shareToTikTok(imageUrl);
      if (result.success) {
        if (result.method === 'download') {
          showMessage(result.message, 'success');
        } else {
          showMessage('Shared successfully!', 'success');
        }
      } else {
        showMessage('Failed to share. Please try downloading instead.', 'error');
      }
    } catch (error) {
      showMessage('Error sharing to TikTok', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    setLoading(true);
    try {
      const success = await downloadImageWithWatermark(imageUrl, `${imageName}-fittty.jpg`);
      if (success) {
        showMessage('Image downloaded with FITTTY watermark!', 'success');
      } else {
        showMessage('Failed to download image', 'error');
      }
    } catch (error) {
      showMessage('Error downloading image', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = () => {
    const success = copyShareLink(imageUrl);
    if (success) {
      showMessage('Link copied to clipboard!', 'success');
    } else {
      showMessage('Failed to copy link', 'error');
    }
  };

  const handleWhatsAppShare = async () => {
    setLoading(true);
    try {
      const result = await shareToWhatsApp(imageUrl);
      if (result.success) {
        showMessage(result.message || 'Shared successfully!', 'success');
      } else {
        if (result.cancelled) {
          showMessage('Share cancelled', 'error');
        } else {
          showMessage(result.details || 'WhatsApp opened with text only', 'success');
        }
      }
    } catch (error) {
      showMessage('Error: ' + (error.message || 'Failed to share'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleFacebookShare = () => {
    shareToFacebook();
    showMessage('Opening Facebook...', 'success');
  };

  const handleTwitterShare = () => {
    shareToTwitter();
    showMessage('Opening Twitter...', 'success');
  };

  return (
    <>
      <div className="share-modal-backdrop" onClick={onClose}></div>
      <div className="share-modal">
        <div className="share-modal-header">
          <h2>Share Your Try-On</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        {message && (
          <div className={`share-message ${messageType}`}>
            {message}
          </div>
        )}

        <div className="share-modal-content">
          <div className="share-preview">
            <img src={imageUrl} alt="Try-on preview" />
            <div className="watermark-notice">
              <span>🏷️</span> Images shared include FITTTY watermark
            </div>
          </div>

          <div className="share-options">
            <h3>Share to Social Media</h3>
            <div className="share-buttons-grid">
              <button
                className="share-btn instagram"
                onClick={handleInstagramShare}
                disabled={loading}
              >
                <span className="share-icon">📷</span>
                <span className="share-label">Instagram</span>
              </button>

              <button
                className="share-btn tiktok"
                onClick={handleTikTokShare}
                disabled={loading}
              >
                <span className="share-icon">🎵</span>
                <span className="share-label">TikTok</span>
              </button>

              <button
                className="share-btn whatsapp"
                onClick={handleWhatsAppShare}
                disabled={loading}
              >
                <span className="share-icon">💬</span>
                <span className="share-label">WhatsApp</span>
              </button>

              <button
                className="share-btn facebook"
                onClick={handleFacebookShare}
                disabled={loading}
              >
                <span className="share-icon">👍</span>
                <span className="share-label">Facebook</span>
              </button>

              <button
                className="share-btn twitter"
                onClick={handleTwitterShare}
                disabled={loading}
              >
                <span className="share-icon">🐦</span>
                <span className="share-label">Twitter</span>
              </button>

              <button
                className="share-btn copy"
                onClick={handleCopyLink}
                disabled={loading}
              >
                <span className="share-icon">🔗</span>
                <span className="share-label">Copy Link</span>
              </button>
            </div>

            <div className="share-divider">
              <span>or</span>
            </div>

            <button
              className="download-btn"
              onClick={handleDownload}
              disabled={loading}
            >
              {loading ? '⏳ Processing...' : '⬇️ Download Image'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ShareModal;
